import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Target,
  Eye,
  Lightbulb,
  Users,
  Award,
  Zap,
  Shield,
  TrendingUp,
  ChevronRight,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import AnimatedCounter from "@/components/shared/AnimatedCounter";
import Eyebrow from "@/components/shared/Eyebrow";

const VALUES = [
  { icon: Lightbulb,  title: "Innovation",   description: "We continuously push boundaries to deliver cutting-edge AI solutions that transform businesses." },
  { icon: Shield,     title: "Integrity",    description: "We maintain the highest ethical standards in all our engagements and recommendations." },
  { icon: Users,      title: "Collaboration",description: "We work closely with our clients, treating their challenges as our own." },
  { icon: TrendingUp, title: "Excellence",   description: "We strive for excellence in every project, ensuring measurable business impact." },
];

const STATS = [
  { number: "50+",  label: "Clients Served" },
  { number: "100+", label: "Projects Delivered" },
  { number: "95%",  label: "Client Satisfaction" },
  { number: "5+",   label: "Years Experience" },
];

const REASONS = [
  "Expertise in AI-powered risk management and loss mitigation",
  "Proven track record with 50+ satisfied clients",
  "Customized solutions tailored to your business needs",
  "End-to-end support from consultation to implementation",
  "Commitment to delivering measurable ROI",
];

const AboutUs = () => {
  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="About Namespace"
        eyebrowIcon={Sparkles}
        title={
          <>
            Building the <span className="gradient-text">future of AI-driven</span> business
          </>
        }
        description="We are a premier consulting firm specializing in AI-driven solutions for risk management, loss mitigation, and business process optimization. Our mission is to empower organizations with intelligent insights that drive growth and efficiency."
        actions={
          <>
            <Button asChild className="btn-primary rounded-full px-6 h-11">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Talk to our team <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
              <Link to="/case-studies">See case studies</Link>
            </Button>
          </>
        }
      />

      {/* Stats */}
      <Section size="sm">
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-8 sm:p-12 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
              {STATS.map((s, i) => (
                <Reveal key={i} variant="up" delay={i * 0.06}>
                  <div className="text-center">
                    <AnimatedCounter value={s.number} label={s.label} />
                  </div>
                </Reveal>
              ))}
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* Vision & Mission */}
      <Section>
        <SectionHeader
          eyebrow="Why we exist"
          eyebrowIcon={Eye}
          title={<>Vision & <span className="gradient-text">Mission</span></>}
        />
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal variant="left">
            <GlowCard className="vision-card p-8 h-full">
              <div className="feature-icon feature-icon-lg mb-6">
                <Eye className="w-6 h-6" />
              </div>
              <h2 className="font-display font-bold text-2xl mb-4">Our Vision</h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                To be the leading catalyst for AI-driven transformation in risk management and business optimization,
                empowering organizations worldwide to achieve operational excellence and sustainable growth through
                intelligent automation and data-driven insights.
              </p>
            </GlowCard>
          </Reveal>
          <Reveal variant="right">
            <GlowCard className="vision-card p-8 h-full">
              <div className="feature-icon feature-icon-lg mb-6">
                <Target className="w-6 h-6" />
              </div>
              <h2 className="font-display font-bold text-2xl mb-4">Our Mission</h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                To deliver innovative, AI-powered solutions that help businesses identify risks, mitigate losses, and
                streamline operations. We are committed to providing actionable insights that drive measurable business
                impact while maintaining the highest standards of integrity and excellence.
              </p>
            </GlowCard>
          </Reveal>
        </div>
      </Section>

      {/* Core Values */}
      <Section>
        <SectionHeader
          eyebrow="Core Values"
          eyebrowIcon={Sparkles}
          title={<>The principles that <span className="gradient-text">drive us</span></>}
          description="These principles guide everything we do and define who we are as a company."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={i} variant="up" delay={i * 0.08}>
              <GlowCard className="text-center p-6 h-full group">
                <div className="feature-icon mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{v.description}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Why choose us */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="p-8 sm:p-12 aurora overflow-hidden">
            <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
              <div>
                <Eyebrow icon={Award} className="mb-4">Why choose us</Eyebrow>
                <h2 className="font-display font-bold text-3xl sm:text-4xl mb-6 leading-tight">
                  Why choose <span className="gradient-text">NameSpace?</span>
                </h2>
                <ul className="space-y-4">
                  {REASONS.map((r, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.5, delay: i * 0.06 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[var(--brand-primary-light)] flex-shrink-0 mt-0.5" />
                      <p className="text-[var(--text-primary)] text-sm sm:text-base">{r}</p>
                    </motion.li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Button asChild className="btn-primary rounded-full px-6 h-11">
                    <Link to="/contact" className="inline-flex items-center gap-2">
                      Start a conversation <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    className="w-72 h-72 rounded-full border border-purple-500/15 absolute inset-0"
                    style={{ borderStyle: "dashed" }}
                  />
                  <div className="w-72 h-72 rounded-full bg-gradient-to-br from-purple-500/15 via-cyan-500/5 to-transparent flex items-center justify-center">
                    <div className="w-44 h-44 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/10 flex items-center justify-center backdrop-blur-md border border-white/10">
                      <Award className="w-20 h-20 text-[var(--brand-primary-light)]" />
                    </div>
                  </div>
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-2 -right-2 w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/30 to-transparent flex items-center justify-center backdrop-blur-md border border-cyan-500/20"
                  >
                    <Zap className="w-7 h-7 text-cyan-300" />
                  </motion.div>
                </div>
              </div>
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* CTA */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-10 text-center max-w-4xl mx-auto">
            <Eyebrow icon={Sparkles} className="mb-4">Get started</Eyebrow>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4 leading-tight">
              Ready to <span className="gradient-text">transform your business?</span>
            </h2>
            <p className="text-[var(--text-secondary)] mb-7 max-w-xl mx-auto">
              Let's discuss how our AI-powered solutions can help you achieve your business goals.
            </p>
            <Button asChild className="btn-primary rounded-full px-8 h-12">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Get in touch <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </GlowCard>
        </Reveal>
      </Section>
    </div>
  );
};

export default AboutUs;
